import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import {
  ArrowLeft,
  Briefcase,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  Copy,
  FileText,
  Globe,
  MapPin,
  MoreVertical,
  // Pencil,
  Users,
} from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { JobService } from 'src/services/api/JobService';
import EngageInstitutionModal from 'src/components/Modals/EngageInstitutionModal';
import { StatusPill } from 'src/components/ui/status-pill';
import MarkdownBody from 'src/components/common/MarkdownBody';
import { cn } from 'src/lib/utils';

// Design-token-based primitives, matching OpportunityDetails.jsx's own
// RoCard/GlanceCell convention (that page is this one's closest sibling —
// both are "detail" pages for the same underlying requirement) rather than
// the raw slate-*/blue-* Tailwind colors + glass-card/backdrop-blur styling
// this page was originally built with. That mismatch was the whole reason
// this page read as visually "off" next to the rest of the app.
const Badge = ({ children, variant = 'default' }) => {
  const styles = {
    default: 'bg-brand-bg-subtle text-primary border-transparent',
    success: 'bg-ds-success-subtle text-ds-success border-transparent',
    warning: 'bg-ds-warning-subtle text-ds-warning border-transparent',
    danger: 'bg-ds-danger-subtle text-ds-danger border-transparent',
    neutral: 'bg-muted text-muted-foreground border-border',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium',
        styles[variant] || styles.default
      )}
    >
      {children}
    </span>
  );
};

// Same shadow/radius language Dashboard.jsx's own DashboardCard uses —
// one shared "card" look across the app instead of this page's own
// rounded-2xl + backdrop-blur glass treatment.
const Card = ({ children, className }) => (
  <div
    className={cn(
      'rounded-xl border border-border bg-card shadow-[0_1px_3px_rgba(0,0,0,.04)]',
      className
    )}
  >
    {children}
  </div>
);

const SectionTitle = ({ icon: Icon, title, right }) => (
  <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
    <div className="flex items-center gap-2.5">
      {Icon ? (
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-bg-subtle text-primary">
          <Icon className="h-4 w-4" />
        </span>
      ) : null}
      <h3 className="text-[13.5px] font-[650] text-foreground-strong">
        {title}
      </h3>
    </div>
    {right ? <div className="shrink-0">{right}</div> : null}
  </div>
);

// Rendered inside a `divide-y divide-border` list (see the sidebar Card
// below) rather than each row drawing its own border — the original
// `subtle`-flagged border classes never actually applied (no `border` width
// utility alongside them), so removing that dead prop here changes nothing
// visually, just the dividers are now real.
const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between gap-3 py-2.5">
    <div className="flex items-center gap-2 text-[11.5px] text-muted-foreground">
      {Icon ? <Icon className="h-3.5 w-3.5 text-primary" /> : null}
      <span>{label}</span>
    </div>
    <div className="text-[13px] font-semibold text-foreground-strong">
      {value}
    </div>
  </div>
);

const KeyValue = ({ k, v }) => (
  <div className="flex flex-col items-start gap-2.5">
    <span className="flex w-full items-center gap-2 text-[12.5px] font-[650] text-foreground-strong">
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-brand-bg-subtle text-primary">
        <MapPin className="h-3.5 w-3.5" />
      </span>
      {k}
    </span>
    <span className="text-sm text-foreground-strong">{v}</span>
  </div>
);

const MiniMenu = ({ actions = [] }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center justify-center rounded-lg border border-input bg-card p-2 text-muted-foreground transition-colors hover:border-primary/30 hover:bg-brand-bg-subtle hover:text-primary"
        aria-label="More"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open ? (
        <div
          className="absolute right-0 z-10 mt-2 w-56 overflow-hidden rounded-xl border border-border bg-card shadow-pop"
          onMouseLeave={() => setOpen(false)}
        >
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={() => {
                setOpen(false);
                action.onClick?.();
              }}
              className="flex w-full items-center gap-2 px-4 py-3 text-sm text-muted-foreground transition-colors hover:bg-brand-bg-subtle hover:text-foreground-strong"
            >
              {action.icon ? (
                <action.icon className="h-4 w-4 text-primary" />
              ) : null}
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const LocationEmpChips = ({ postings }) => {
  const safe = Array.isArray(postings) ? postings : [];
  if (safe.length === 0) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {safe.map((posting) => (
        <span
          key={`${posting?.location || 'loc'}-${posting?.employmentType || 'emp'}`}
          className="inline-flex items-center gap-1.5 rounded-full border border-input bg-brand-bg-subtle px-3 py-1 text-xs font-medium text-primary"
          title={`${posting?.location || ''} · ${posting?.employmentType || ''}`}
        >
          <span>{posting?.location || '—'}</span>
          <span className="opacity-60">·</span>
          <span>{posting?.employmentType || '—'}</span>
        </span>
      ))}
    </div>
  );
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

// "mid_level" -> "Mid Level" — the career-level/experience enum this page
// reads (work_experience / career_level) comes back snake_case from the
// backend, same transform tabFormatters.js's own humanizeStatus uses
// elsewhere in this app for the identical "raw enum string" problem.
const humanizeUnderscored = (value) => {
  if (typeof value !== 'string' || !value.includes('_')) return value;
  return value
    .split('_')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
};

const toTextArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item;
        return (
          item?.name ||
          item?.label ||
          item?.value ||
          item?.text ||
          item?.description ||
          ''
        );
      })
      .filter(Boolean);
  }
  if (typeof value === 'string') return [value];
  return [];
};

const getPostings = (job) => {
  if (!Array.isArray(job?.locations)) return [];
  return job.locations
    .map((entry) => ({
      location:
        entry?.location_name ||
        entry?.location?.value ||
        entry?.location ||
        entry?.name ||
        '—',
      employmentType:
        entry?.employment_type || entry?.employmentType || entry?.type || '—',
    }))
    .filter((entry) => entry.location !== '—' || entry.employmentType !== '—');
};

const getSalary = (job) => {
  if (job?.salary) return job.salary;
  const min = job?.minimum_salary;
  const max = job?.maximum_salary;
  if (min || max) return `${min || '—'} - ${max || '—'}`;
  return '—';
};

const mapSkillsToTags = (skills = []) =>
  (Array.isArray(skills) ? skills : [])
    .map((item) => item?.skill_name || item?.name || item?.label || '')
    .filter(Boolean);

const extractJobPayload = (response) => {
  const payload = response?.data ?? response;
  return payload?.job || payload?.data || payload;
};

const normalizeJob = (rawJob, fallbackId) => {
  const safeJob = rawJob || {};
  const requirementInfo = safeJob.requirement_info || {};
  const mappedSkills = mapSkillsToTags(requirementInfo.skills);
  const description = toTextArray(
    safeJob.description ||
      safeJob.description_points ||
      safeJob.what_youll_do ||
      safeJob.job_description
  );

  return {
    id: String(
      safeJob.id || safeJob.job_id || safeJob.jobId || fallbackId || '—'
    ),
    jobCode: safeJob.job_code || '',
    registrationId: requirementInfo.registration_id || '',
    title: safeJob.title || safeJob.job_title || safeJob.name || 'Untitled Job',
    company: safeJob.organization_name || 'Ahedge',
    department:
      safeJob.department ||
      safeJob.business_unit ||
      requirementInfo.business_unit ||
      '—',
    workMode:
      safeJob.work_mode || safeJob.workMode || requirementInfo.work_mode || '—',
    experience:
      safeJob.work_experience ||
      safeJob.experience ||
      requirementInfo.career_level ||
      '—',
    salary: getSalary(safeJob),
    openings: safeJob.numbers_required || safeJob.openings || 0,
    status: safeJob.status || requirementInfo.status || 'Open',
    createdAt: safeJob.created_at || safeJob.createdAt || null,
    postedOn:
      safeJob.posted_on ||
      safeJob.postedOn ||
      requirementInfo.posted_on ||
      null,
    deadline:
      safeJob.closing_date ||
      safeJob.deadline ||
      requirementInfo.closing_date ||
      null,
    remoteAllowed: Boolean(safeJob.remote_allowed || safeJob.remoteAllowed),
    jobUrl: safeJob.job_url || safeJob.jobUrl || '',
    postings: getPostings(safeJob),
    tags: mappedSkills.length
      ? mappedSkills
      : toTextArray(safeJob.tags || safeJob.skills),
    summary:
      safeJob.summary ||
      safeJob.job_description ||
      safeJob.detailed_job_description ||
      '—',
    description,
    responsibilities: toTextArray(safeJob.responsibilities),
    stats: {
      applicants:
        safeJob.applicants ??
        safeJob.applicant_count ??
        safeJob.stats?.applicants ??
        0,
    },
    activity: Array.isArray(safeJob.activity)
      ? safeJob.activity.map((item, index) => ({
          at:
            item?.at || item?.created_at || item?.timestamp || `#${index + 1}`,
          by: item?.by || item?.actor || item?.user || 'System',
          text: item?.text || item?.message || item?.description || 'Updated',
        }))
      : [],
  };
};

const BackButton = ({ onClick }) => (
  <Button
    variant="outline"
    size="headerSm"
    className="gap-1.5"
    onClick={onClick}
  >
    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} />
    Back
  </Button>
);

const JobPostingDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [isEngageInstitutionModalOpen, setIsEngageInstitutionModalOpen] =
    useState(false);

  const fetchJobDetails = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setErrorMessage('');
    try {
      const response = await JobService.getJobDetails(id);
      const payload = extractJobPayload(response);
      setJob(normalizeJob(payload, id));
    } catch (error) {
      console.error('Failed to load job details:', error);
      setErrorMessage('Unable to load job details. Please try again.');
      setJob(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchJobDetails();
  }, [fetchJobDetails]);

  const statusMeta = useMemo(() => {
    const status = (job?.status || '').toLowerCase();
    if (status === 'open') {
      return { scheme: 'emerald', label: 'Open' };
    }
    if (status === 'on hold' || status === 'hold') {
      return { scheme: 'amber', label: 'On Hold' };
    }
    return {
      scheme: 'red',
      label: job?.status || 'Closed',
    };
  }, [job?.status]);

  const backToList = () => navigate('/job-postings');

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Job Posting Details"
          actions={<BackButton onClick={backToList} />}
        />
        <div
          className="flex items-center justify-center bg-muted"
          style={{ minHeight: 'calc(100vh - 3.4rem)' }}
        >
          <p className="text-[13px] text-muted-foreground">
            Loading job details…
          </p>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div>
        <PageHeader
          title="Job Posting Details"
          actions={<BackButton onClick={backToList} />}
        />
        <div
          className="flex items-center justify-center bg-muted"
          style={{ minHeight: 'calc(100vh - 3.4rem)' }}
        >
          <div className="max-w-[360px] rounded-xl border border-destructive/20 bg-card p-6 text-center">
            <div className="mb-1.5 text-sm font-semibold text-destructive">
              Error loading job details
            </div>
            <div className="mb-4 text-[13px] text-muted-foreground">
              {errorMessage}
            </div>
            <Button size="sm" onClick={fetchJobDetails}>
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div>
        <PageHeader
          title="Job Posting Details"
          actions={<BackButton onClick={backToList} />}
        />
        <div
          className="flex items-center justify-center bg-muted"
          style={{ minHeight: 'calc(100vh - 3.4rem)' }}
        >
          <p className="text-[13px] text-muted-foreground">
            No job details found.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Job Posting Details"
        actions={<BackButton onClick={backToList} />}
      />
      <PageContainer>
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-xl font-bold text-foreground-strong">
                {job.title}
              </h1>
              <StatusPill
                colorScheme={statusMeta.scheme}
                name={statusMeta.label}
              />
            </div>

            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Building2 className="h-4 w-4 text-primary" />
                {job.company}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-primary" />
                {job.postings?.length
                  ? `${job.postings.length} location options`
                  : '—'}
              </span>
              {job.jobCode ? (
                <Badge variant="neutral">Job code: {job.jobCode}</Badge>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="inline-flex min-w-[110px] items-center justify-center gap-2 rounded-lg border border-input bg-card px-4 py-2 text-sm font-semibold text-muted-foreground transition-all hover:border-primary/30 hover:bg-brand-bg-subtle active:scale-[0.98]"
              onClick={() => {
                navigator.clipboard?.writeText(job.jobUrl)?.catch(() => {});
                setIsCopied(true);
                setTimeout(() => setIsCopied(false), 2000);
              }}
            >
              {isCopied ? (
                <CheckCircle2 className="h-4 w-4 text-ds-success" />
              ) : (
                <Copy className="h-4 w-4 text-primary" />
              )}
              {isCopied ? 'Copied' : 'Copy link'}
            </button>
            <MiniMenu
              actions={[
                {
                  label: 'View public page',
                  icon: Globe,
                  onClick: () => {
                    if (!job.jobUrl) return;
                    window.open(job.jobUrl, '_blank', 'noopener,noreferrer');
                  },
                },
                {
                  label: 'Engage Institution',
                  icon: Building2,
                  onClick: () => {
                    setIsEngageInstitutionModalOpen(true);
                  },
                },
              ]}
            />
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-[5fr_2fr]">
          <Card>
            <SectionTitle icon={FileText} title="Job Summary" />
            <div className="px-5 pb-5">
              <div className="mt-2 text-sm leading-6 text-muted-foreground">
                {/* detailed_job_description comes back as markdown source
                    (headers/bullets/**bold**) — same renderer
                    OpportunityDetails.jsx uses for the identical field,
                    extracted to MarkdownBody so both share one parser. */}
                <MarkdownBody source={job.summary} />
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {job.tags?.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-input bg-brand-bg-subtle px-3 py-1 text-xs font-medium text-primary"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </Card>

          <div className="flex flex-col gap-4">
            <Card className="divide-y divide-border px-4">
              <Stat
                icon={Users}
                label="Applicants"
                value={job.stats.applicants}
              />
              <Stat
                icon={Briefcase}
                label="Experience"
                value={humanizeUnderscored(job.experience)}
              />
              <Stat
                icon={Calendar}
                label="Deadline"
                value={formatDate(job.deadline)}
              />
              <Stat
                icon={Clock}
                label="Posted on"
                value={formatDate(job.postedOn)}
              />
            </Card>
            <Card className="p-4">
              <KeyValue
                k="Locations"
                v={<LocationEmpChips postings={job.postings} />}
              />
            </Card>
          </div>
        </div>
      </PageContainer>
      <EngageInstitutionModal
        isOpen={isEngageInstitutionModalOpen}
        onClose={() => setIsEngageInstitutionModalOpen(false)}
        jobCode={job?.jobCode}
      />
    </div>
  );
};

export default JobPostingDetail;
